import React from 'react';
import {
  ToggleLeft,
  Globe,
  Settings,
  ToggleRight
} from 'lucide-react';
import { Document } from '../../types';
import { format } from 'date-fns';
import Pagination from './Pagination';

interface DocumentsTableProps {
  documents: Document[];
  selectedDocuments: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectDocument: (documentId: string, checked: boolean) => void;
  onDocumentClick: (documentId: string) => void;
  onToggleDocument: (documentId: string) => void;
  // Pagination props
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  isSelectAll: boolean;
}

const DocumentsTable: React.FC<DocumentsTableProps> = ({
  documents,
  selectedDocuments,
  onSelectAll,
  onSelectDocument,
  onDocumentClick,
  onToggleDocument,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  isSelectAll
}) => {

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto overflow-y-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-12 px-3 sm:px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={isSelectAll}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                NAME
              </th>
              <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                CHUNKING MODE
              </th>
              <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                WORDS
              </th>
              <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                UPLOAD TIME
              </th>
              <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                STATUS
              </th>
              <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                ACTION
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {documents.map((document, index) => (
              <tr
                key={document.id}
                className={`cursor-pointer transition-colors ${selectedDocuments.includes(document.id)
                  ? 'bg-blue-50 hover:bg-blue-100 border-l-4 border-blue-500'
                  : 'hover:bg-gray-50'
                  }`}
                onClick={() => onDocumentClick(document.id)}
              >
                <td className="px-3 sm:px-4 py-3 sm:py-4">
                  <input
                    type="checkbox"
                    checked={selectedDocuments.includes(document.id)}
                    onChange={(e) => onSelectDocument(document.id, e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className="px-3 sm:px-4 py-3 sm:py-4">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <Globe className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">{document.name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 sm:px-4 py-3 sm:py-4">
                  <div className="flex items-center">
                    <span className="text-[9px] sm:text-[10px] text-gray-900 border border-gray-300 p-1 rounded-md whitespace-nowrap">
                      <Settings className="inline-block h-2 w-2 sm:h-3 sm:w-3" />
                      <span className="hidden sm:inline">{document.chunkingMode || 'GENERAL'}</span>
                      <span className="sm:hidden">GENERAL</span>
                    </span>
                  </div>
                </td>
                <td className="px-3 sm:px-4 py-3 sm:py-4 text-xs sm:text-sm text-gray-900">
                  <span className="whitespace-nowrap">{document?.wordCount?.toLocaleString() || '0'}</span>
                </td>
                <td className="px-3 sm:px-4 py-3 sm:py-4 text-xs sm:text-sm text-gray-900">
                  <span className="whitespace-nowrap">
                    <span className="">{format(new Date(document.creationTime), 'MMM dd, yyyy')}</span>
                  </span>
                </td>
                <td className="px-3 sm:px-4 py-3 sm:py-4">
                  <div className="flex items-center whitespace-nowrap">
                    {(document.enabled && document.status !== 'Queuing') ? (
                      <>
                        <span className="text-green-500 me-1">●</span>
                        <span className="text-xs sm:text-sm text-green-500">
                          <span className="">Enabled</span>
                        </span>
                      </>
                    ) : (!document.enabled && document.status !== 'Queuing') ? (
                      <>
                        <span className="text-gray-500 me-1">●</span>
                        <span className="text-xs sm:text-sm text-gray-500">
                          <span className="">Disabled</span>
                        </span>
                      </>
                    ) : (<>
                      <span className="text-orange-400 me-1">●</span>
                      <span className="text-xs sm:text-sm text-orange-400">
                        <span className="">Queuing</span>
                      </span>
                    </>)}
                  </div>
                </td>
                <td className="px-3 sm:px-4 py-3 sm:py-4">
                  <div className="flex items-center justify-start">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleDocument(document.id);
                      }}
                      className={`p-1 rounded transition-colors ${document.enabled
                        ? 'text-blue-600 hover:text-blue-800'
                        : 'text-gray-400 hover:text-gray-600'
                        }`}
                      title={document.enabled ? 'Disable' : 'Enable'}
                    >
                      {document.enabled ? <ToggleRight className="w-4 h-4 sm:w-4 sm:h-4" /> : <ToggleLeft className="w-4 h-4 sm:w-4 sm:h-4" />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {documents.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 sm:px-4 py-8 text-center text-sm text-gray-500">
                  No documents found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={onPageChange}
        onItemsPerPageChange={onItemsPerPageChange}
      />
    </div>
  );
};

export default DocumentsTable;
